import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { useStorage } from '@vueuse/core';

import getEnv from '@/utils/env';
import apiGw from '@/services/apiGw';
import { REFRESH_TOKEN_EXP } from '@/utils/general';
import { EnvironmentTypes } from '@/types/environmentTypes';

interface ParsedToken {
  email: string;
  exp: number;
}

export enum AuthLevel {
  None = 'none',
  Pin = 'pin',
  Weak = 'weak',
  Strong = 'strong'
}

function getCookies(): { [key: string]: string } {
  const cookies = document.cookie.split('; ').reduce((res, item) => {
    const data = item.trim().split('=');
    return { ...res, [data[0]]: data[1] };
  }, {});
  return cookies;
}

function getParsedToken(): ParsedToken | null {
  const authDetails = getCookies()[getEnv(EnvironmentTypes.VITE_APP_ENV) + 'YapealAuthDetails'];

  if (authDetails) {
    // base64 decode the payload if the token
    return JSON.parse(atob(authDetails.split('.')[1]));
  }
  return null;
}

function isProspectEmail(): boolean {
  const token = getParsedToken();
  if (!token) {
    return false;
  }
  const prospectEmailRegex = /^yap[0-9a-fA-F-]{36}@yapeal\.ch$/;
  return prospectEmailRegex.test(token.email);
}

function isTokenExpired(token: ParsedToken): boolean {
  const expiration = new Date(token.exp * 1000);
  const now = new Date();
  return now > expiration;
}

function isAuthenticated(): boolean {
  const token = getParsedToken();
  if (!token || isProspectEmail()) {
    return false;
  }

  return !isTokenExpired(token);
}

export const useAuthStore = defineStore('auth', () => {
  // STATES
  const authenticated = ref(isAuthenticated());
  const prospectVerified = useStorage('prospectVerified', false);
  const prospectAuthenticated = ref(isProspectAuthenticated());
  const refreshTokenExp = ref(localStorage.getItem(REFRESH_TOKEN_EXP) ?? undefined);
  // GETTERS
  const authLevel = computed(() => {
    if (authenticated.value) {
      return AuthLevel.Strong;
    } else if (prospectAuthenticated.value) {
      return AuthLevel.Pin;
    } else {
      return AuthLevel.None;
    }
  });

  // ACTIONS
  function $reset(): void {
    const env = getEnv(EnvironmentTypes.VITE_APP_ENV);
    const domain = env === 'local' ? 'localhost' : '.yapeal.ch';
    document.cookie = `${env}YapealAuthDetails=; Max-Age=0; path=/; domain=${domain};`;

    authenticated.value = false;
  }

  function checkIsAuthenticated(): boolean {
    authenticated.value = isAuthenticated();
    return authenticated.value;
  }
  function checkIsProspectAuthenticated(): boolean {
    console.debug('in checkIsProspectAuthenticated');
    prospectAuthenticated.value = isProspectAuthenticated();
    return prospectAuthenticated.value;
  }

  function setAuthenticated(isAuthed: boolean): void {
    authenticated.value = isAuthed;
  }

  function removeRefreshTokenExp(): void {
    refreshTokenExp.value = undefined;
    localStorage.removeItem(REFRESH_TOKEN_EXP);
  }

  function setRefreshTokenExp(exp: string): void {
    refreshTokenExp.value = exp;
    if (exp !== localStorage.getItem(REFRESH_TOKEN_EXP)) {
      localStorage.setItem(REFRESH_TOKEN_EXP, exp);
    }
  }

  async function refreshToken(): Promise<void> {
    try {
      const rsp = await apiGw.post('/v3/auth/refresh');
      setRefreshTokenExp(rsp.headers['x-refresh-token-exp']);
    } catch (error) {
      console.error('Something went wrong while calling refrshToken', error);
    }
  }

  function isProspectAuthenticated(): boolean {
    const token = getParsedToken();
    if (!token) {
      prospectVerified.value = false;
      return false;
    }

    const authed = isProspectEmail() && !isTokenExpired(token);
    if (!authed) {
      prospectVerified.value = false;
    }
    return authed;
  }

  return {
    authenticated,
    prospectAuthenticated,
    prospectVerified,
    refreshTokenExp,
    authLevel,
    $reset,
    checkIsAuthenticated,
    checkIsProspectAuthenticated,
    setAuthenticated,
    removeRefreshTokenExp,
    setRefreshTokenExp,
    refreshToken
  };
});
